import React, { PropsWithChildren, useEffect, useMemo, useState } from 'react';

import { DictionaryItemDto, DictionaryTypeDto } from '@/api/generated';
import { useGetAllDictionaries } from '@/api/requests/useGetAllDictionaries.util.ts';
import { SelectSearchOptionModel } from '@/components/form/model/SelectSearchOption.model.ts';
import { FullPageSpinner } from '@/components/ui/fullPageSpinner/FullPageSpinner.comp';

import {
  DictionariesContext,
  DictionariesContextProps,
  DictionariesDefaultValues,
} from './Dictionaries.context';

const mapDict = (dict: DictionaryItemDto): SelectSearchOptionModel<string> => ({
  label: dict.label,
  value: dict.code,
});

export const DictionariesContextProvider: React.FC<PropsWithChildren> = props => {
  const [groupedDictionaries, setGroupedDictionaries] = useState<DictionariesContextProps>({
    ...DictionariesDefaultValues,
  });

  const { dictionaries, isDictionariesError, isFetchingDictionariesTypes } =
    useGetAllDictionaries();

  useEffect(() => {
    if (dictionaries) {
      setGroupedDictionaries({
        [DictionaryTypeDto.CompetencyArea]:
          dictionaries[DictionaryTypeDto.CompetencyArea].map(mapDict),
        [DictionaryTypeDto.Department]: dictionaries[DictionaryTypeDto.Department].map(mapDict),
        [DictionaryTypeDto.EmployeeAspirationInTime]:
          dictionaries[DictionaryTypeDto.EmployeeAspirationInTime].map(mapDict),
        [DictionaryTypeDto.EmployeeAspirationType]:
          dictionaries[DictionaryTypeDto.EmployeeAspirationType].map(mapDict),
        [DictionaryTypeDto.EmployeeLeavingRiskLevel]:
          dictionaries[DictionaryTypeDto.EmployeeLeavingRiskLevel].map(mapDict),
        [DictionaryTypeDto.EmployeeMobility]:
          dictionaries[DictionaryTypeDto.EmployeeMobility].map(mapDict),
        [DictionaryTypeDto.EmployeePosition]:
          dictionaries[DictionaryTypeDto.EmployeePosition].map(mapDict),
        [DictionaryTypeDto.EmployeeSuccessionReadiness]:
          dictionaries[DictionaryTypeDto.EmployeeSuccessionReadiness].map(mapDict),
        [DictionaryTypeDto.EnglishLevel]: dictionaries[DictionaryTypeDto.EnglishLevel].map(mapDict),
        [DictionaryTypeDto.Matrix]: dictionaries[DictionaryTypeDto.Matrix].map(mapDict),
        [DictionaryTypeDto.Region]: dictionaries[DictionaryTypeDto.Region].map(mapDict),
        // Słownik sklepów (1567 pozycji) — celowo NIE ładowany przez globalny kontekst.
        // Korzysta z lazy infinite-scroll dropdowna w miejscach docelowych (np. CreateExamDayDialog).
        [DictionaryTypeDto.TraditionalCounterShop]: [],
        [DictionaryTypeDto.UserRole]: dictionaries[DictionaryTypeDto.UserRole].map(mapDict),
        [DictionaryTypeDto.YesNo]: dictionaries[DictionaryTypeDto.YesNo].map(mapDict),
      });
    }
  }, [Boolean(dictionaries)]);

  const context: DictionariesContextProps = useMemo(() => {
    return {
      ...groupedDictionaries,
    };
  }, [groupedDictionaries]);

  if (isFetchingDictionariesTypes || isDictionariesError) {
    return <FullPageSpinner />;
  }

  return (
    <>
      <DictionariesContext.Provider value={context}>{props.children}</DictionariesContext.Provider>
    </>
  );
};
